import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User,
  Bell,
  Mic,
  Moon,
  Sun,
  Monitor,
  Shield,
  Save,
  Check,
  ChevronRight,
  LogOut,
  GraduationCap,
  Brain,
  Trash2,
  Eye,
  EyeOff,
  X,
  AlertCircle
} from 'lucide-react';
import { updatePassword } from 'aws-amplify/auth';
import { AppShell, GlassPanel } from '@/components/layout';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore, type Theme } from '@/stores/themeStore';
import { userApi } from '@/lib/api';
import { cn } from '@/lib/cn';
import { staggerContainer, staggerItem } from '@/lib/motion';

interface SettingSection {
  id: string;
  title: string;
  icon: React.ElementType;
  description: string;
}

const sections: SettingSection[] = [
  { id: 'profile', title: 'Profile', icon: User, description: 'Manage your account details' },
  { id: 'experience', title: 'Experience Level', icon: GraduationCap, description: 'Set your maritime expertise' },
  { id: 'voice', title: 'Voice & Audio', icon: Mic, description: 'Configure voice assistant settings' },
  { id: 'notifications', title: 'Notifications', icon: Bell, description: 'Control how you receive alerts' },
  { id: 'appearance', title: 'Appearance', icon: Moon, description: 'Customize the app look' },
  { id: 'privacy', title: 'Privacy & Security', icon: Shield, description: 'Manage your data and security' },
];

/**
 * Settings Page - User preferences and configuration
 */
export function Settings() {
  const { user, logout, updateUser } = useAuthStore();
  const { theme, setTheme, compactMode, setCompactMode, animationsEnabled, setAnimationsEnabled } = useThemeStore();
  const [activeSection, setActiveSection] = useState('profile');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Experience level state
  const [experienceLevel, setExperienceLevel] = useState<'trainee' | 'junior' | 'senior'>(
    user?.experience_level || 'junior'
  );

  // Form states
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    company: user?.company || '',
    job_title: user?.job_title || '',
  });

  const [voiceSettings, setVoiceSettings] = useState({
    voiceSpeed: 1.0,
    voicePitch: 1.0,
    autoListen: false,
    silenceDetection: true,
    noiseReduction: true,
  });

  const [notifications, setNotifications] = useState({
    emailAlerts: true,
    pushNotifications: false,
    sessionReminders: true,
    marketUpdates: true,
  });

  // Appearance settings now come from themeStore (BUG-008 fix)
  // compactMode and animationsEnabled are persisted in themeStore

  // Memory clearing state
  const [clearingMemory, setClearingMemory] = useState(false);
  const [memoryCleared, setMemoryCleared] = useState(false);

  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Sync all settings from user profile (BUG-005, 006, 007 fix)
  useEffect(() => {
    if (user) {
      // Profile settings
      setProfile({
        name: user.name || '',
        email: user.email || '',
        company: user.company || '',
        job_title: user.job_title || '',
      });
      setExperienceLevel(user.experience_level || 'junior');

      // Voice settings (BUG-006)
      setVoiceSettings({
        voiceSpeed: user.voice_speed ?? 1.0,
        voicePitch: user.voice_pitch ?? 1.0,
        autoListen: user.auto_listen ?? false,
        silenceDetection: user.silence_detection ?? true,
        noiseReduction: user.noise_reduction ?? true,
      });

      // Notification settings (BUG-007)
      setNotifications({
        emailAlerts: user.email_alerts ?? true,
        pushNotifications: user.push_notifications ?? false,
        sessionReminders: user.session_reminders ?? true,
        marketUpdates: user.market_updates ?? true,
      });
    }
  }, [user]);

  const handleClearMemory = async () => {
    if (!confirm('Are you sure you want to clear your memory? DryVox will forget all learned preferences from previous sessions.')) {
      return;
    }

    setClearingMemory(true);
    try {
      const { data } = await userApi.updateMe({ clear_memory: true });
      updateUser(data);
      setMemoryCleared(true);
      setTimeout(() => setMemoryCleared(false), 3000);
    } catch (error) {
      console.error('Failed to clear memory:', error);
      alert('Failed to clear memory. Please try again.');
    } finally {
      setClearingMemory(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build update payload with all changed settings
      const updatePayload: Record<string, unknown> = {};

      // Profile settings (BUG-005)
      if (profile.name !== user?.name) updatePayload.name = profile.name;
      if (profile.company !== user?.company) updatePayload.company = profile.company;
      if (profile.job_title !== user?.job_title) updatePayload.job_title = profile.job_title;

      // Experience level
      if (experienceLevel !== user?.experience_level) {
        updatePayload.experience_level = experienceLevel;
      }

      // Voice settings (BUG-006)
      if (voiceSettings.voiceSpeed !== user?.voice_speed) {
        updatePayload.voice_speed = voiceSettings.voiceSpeed;
      }
      if (voiceSettings.voicePitch !== user?.voice_pitch) {
        updatePayload.voice_pitch = voiceSettings.voicePitch;
      }
      if (voiceSettings.autoListen !== user?.auto_listen) {
        updatePayload.auto_listen = voiceSettings.autoListen;
      }
      if (voiceSettings.silenceDetection !== user?.silence_detection) {
        updatePayload.silence_detection = voiceSettings.silenceDetection;
      }
      if (voiceSettings.noiseReduction !== user?.noise_reduction) {
        updatePayload.noise_reduction = voiceSettings.noiseReduction;
      }

      // Notification settings (BUG-007)
      if (notifications.emailAlerts !== user?.email_alerts) {
        updatePayload.email_alerts = notifications.emailAlerts;
      }
      if (notifications.pushNotifications !== user?.push_notifications) {
        updatePayload.push_notifications = notifications.pushNotifications;
      }
      if (notifications.sessionReminders !== user?.session_reminders) {
        updatePayload.session_reminders = notifications.sessionReminders;
      }
      if (notifications.marketUpdates !== user?.market_updates) {
        updatePayload.market_updates = notifications.marketUpdates;
      }

      // Only call API if there are changes
      if (Object.keys(updatePayload).length > 0) {
        const { data } = await userApi.updateMe(updatePayload);
        updateUser(data);
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  // Export user data (BUG-009 fix)
  const [exporting, setExporting] = useState(false);

  const handleExportData = async () => {
    setExporting(true);
    try {
      const { data } = await userApi.exportData();
      // Create downloadable file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dryvox-export-${user?.id?.slice(0, 8) || 'data'}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export data:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError(null);

    // Validation
    if (!passwordForm.currentPassword) {
      setPasswordError('Current password is required');
      return;
    }
    if (!passwordForm.newPassword) {
      setPasswordError('New password is required');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    setChangingPassword(true);
    try {
      // Use Cognito to change password
      await updatePassword({
        oldPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });

      setPasswordSuccess(true);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });

      // Close modal after showing success
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess(false);
      }, 2000);
    } catch (error: any) {
      console.error('Password change error:', error);

      let message = 'Failed to change password. Please try again.';
      if (error.name === 'NotAuthorizedException') {
        message = 'Current password is incorrect';
      } else if (error.name === 'InvalidPasswordException') {
        message = 'New password does not meet requirements (min 8 chars, uppercase, lowercase, number)';
      } else if (error.name === 'LimitExceededException') {
        message = 'Too many attempts. Please try again later.';
      } else if (error.message) {
        message = error.message;
      }

      setPasswordError(message);
    } finally {
      setChangingPassword(false);
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError(null);
    setPasswordSuccess(false);
  };

  return (
    <AppShell>
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        className="space-y-6"
      >
        {/* Header */}
        <motion.div variants={staggerItem}>
          <h1 className="text-2xl md:text-3xl font-display font-bold text-white">
            Settings
          </h1>
          <p className="text-gray-400 mt-1">
            Manage your preferences and account settings
          </p>
        </motion.div>

        <div className="grid md:grid-cols-[280px_1fr] gap-6">
          {/* Sidebar Navigation */}
          <motion.div variants={staggerItem}>
            <GlassPanel className="p-2" animate={false}>
              <nav className="space-y-1">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;

                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-left',
                        isActive
                          ? 'bg-ocean-cyan/20 text-ocean-cyan'
                          : 'text-white/70 hover:text-white hover:bg-white/5'
                      )}
                    >
                      <Icon className="w-5 h-5" />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{section.title}</p>
                        <p className="text-xs text-white/40 line-clamp-1">
                          {section.description}
                        </p>
                      </div>
                      {isActive && (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                  );
                })}

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-all text-left mt-4"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium text-sm">Logout</span>
                </button>
              </nav>
            </GlassPanel>
          </motion.div>

          {/* Content */}
          <motion.div variants={staggerItem}>
            <GlassPanel className="p-6" animate={false}>
              {/* Profile Section */}
              {activeSection === 'profile' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-white mb-1">Profile Settings</h2>
                    <p className="text-sm text-white/50">Update your personal information</p>
                  </div>

                  <div className="grid gap-4">
                    <div>
                      <label className="block text-sm text-white/60 mb-2">Full Name</label>
                      <input
                        type="text"
                        value={profile.name}
                        onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                        className={cn(
                          'w-full px-4 py-3 rounded-xl',
                          'bg-white/5 border border-white/10',
                          'text-white placeholder-white/30',
                          'focus:outline-none focus:ring-2 focus:ring-ocean-cyan/50'
                        )}
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-white/60 mb-2">Email</label>
                      <input
                        type="email"
                        value={profile.email}
                        onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                        className={cn(
                          'w-full px-4 py-3 rounded-xl',
                          'bg-white/5 border border-white/10',
                          'text-white placeholder-white/30',
                          'focus:outline-none focus:ring-2 focus:ring-ocean-cyan/50'
                        )}
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-white/60 mb-2">Company</label>
                      <input
                        type="text"
                        value={profile.company}
                        onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                        className={cn(
                          'w-full px-4 py-3 rounded-xl',
                          'bg-white/5 border border-white/10',
                          'text-white placeholder-white/30',
                          'focus:outline-none focus:ring-2 focus:ring-ocean-cyan/50'
                        )}
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-white/60 mb-2">Job Title</label>
                      <input
                        type="text"
                        value={profile.job_title}
                        onChange={(e) => setProfile({ ...profile, job_title: e.target.value })}
                        className={cn(
                          'w-full px-4 py-3 rounded-xl',
                          'bg-white/5 border border-white/10',
                          'text-white placeholder-white/30',
                          'focus:outline-none focus:ring-2 focus:ring-ocean-cyan/50'
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Experience Level Section */}
              {activeSection === 'experience' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-white mb-1">Experience Level</h2>
                    <p className="text-sm text-white/50">
                      Set your maritime expertise level to customize how DryVox interacts with you
                    </p>
                  </div>

                  <div className="space-y-3">
                    {[
                      {
                        value: 'trainee' as const,
                        label: 'Trainee',
                        description: "I'm new to dry bulk shipping. Explain concepts from basics, define all maritime terminology.",
                      },
                      {
                        value: 'junior' as const,
                        label: 'Junior',
                        description: 'I understand standard terminology. Focus on operations, procedures, and practical tips.',
                      },
                      {
                        value: 'senior' as const,
                        label: 'Senior',
                        description: "I'm experienced. Be concise and strategic. Skip basics, focus on market nuance.",
                      },
                    ].map(({ value, label, description }) => (
                      <button
                        key={value}
                        onClick={() => setExperienceLevel(value)}
                        className={cn(
                          'w-full flex items-start gap-4 p-4 rounded-xl text-left transition-all',
                          experienceLevel === value
                            ? 'bg-ocean-cyan/20 border-2 border-ocean-cyan/50'
                            : 'bg-white/5 border border-white/10 hover:border-white/20'
                        )}
                      >
                        <div
                          className={cn(
                            'w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center',
                            experienceLevel === value
                              ? 'border-ocean-cyan bg-ocean-cyan'
                              : 'border-white/30'
                          )}
                        >
                          {experienceLevel === value && (
                            <div className="w-2 h-2 rounded-full bg-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className={cn(
                            'font-medium',
                            experienceLevel === value ? 'text-ocean-cyan' : 'text-white'
                          )}>
                            {label}
                          </p>
                          <p className="text-sm text-white/50 mt-1">{description}</p>
                        </div>
                      </button>
                    ))}
                  </div>

                  <div className="p-4 rounded-xl bg-ocean-blue/10 border border-ocean-blue/20">
                    <p className="text-sm text-white/70">
                      <strong className="text-ocean-cyan">Tip:</strong> Your experience level affects how DryVox explains concepts,
                      the level of detail in responses, and the type of verification reminders you receive.
                    </p>
                  </div>
                </div>
              )}

              {/* Voice & Audio Section */}
              {activeSection === 'voice' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-white mb-1">Voice & Audio</h2>
                    <p className="text-sm text-white/50">Configure voice assistant behavior</p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-white/60 mb-2">
                        Voice Speed: {voiceSettings.voiceSpeed.toFixed(1)}x
                      </label>
                      <input
                        type="range"
                        min="0.5"
                        max="2"
                        step="0.1"
                        value={voiceSettings.voiceSpeed}
                        onChange={(e) => setVoiceSettings({ ...voiceSettings, voiceSpeed: parseFloat(e.target.value) })}
                        className="w-full accent-ocean-cyan"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-white/60 mb-2">
                        Voice Pitch: {voiceSettings.voicePitch.toFixed(1)}
                      </label>
                      <input
                        type="range"
                        min="0.5"
                        max="2"
                        step="0.1"
                        value={voiceSettings.voicePitch}
                        onChange={(e) => setVoiceSettings({ ...voiceSettings, voicePitch: parseFloat(e.target.value) })}
                        className="w-full accent-ocean-cyan"
                      />
                    </div>

                    <ToggleOption
                      label="Auto-listen after response"
                      description="Automatically start listening after the assistant speaks"
                      checked={voiceSettings.autoListen}
                      onChange={(checked) => setVoiceSettings({ ...voiceSettings, autoListen: checked })}
                    />

                    <ToggleOption
                      label="Silence detection"
                      description="Stop recording when silence is detected"
                      checked={voiceSettings.silenceDetection}
                      onChange={(checked) => setVoiceSettings({ ...voiceSettings, silenceDetection: checked })}
                    />

                    <ToggleOption
                      label="Noise reduction"
                      description="Filter out background noise during recording"
                      checked={voiceSettings.noiseReduction}
                      onChange={(checked) => setVoiceSettings({ ...voiceSettings, noiseReduction: checked })}
                    />
                  </div>
                </div>
              )}

              {/* Notifications Section */}
              {activeSection === 'notifications' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-white mb-1">Notifications</h2>
                    <p className="text-sm text-white/50">Control how you receive updates</p>
                  </div>

                  <div className="space-y-4">
                    <ToggleOption
                      label="Email alerts"
                      description="Receive important updates via email"
                      checked={notifications.emailAlerts}
                      onChange={(checked) => setNotifications({ ...notifications, emailAlerts: checked })}
                    />

                    <ToggleOption
                      label="Push notifications"
                      description="Get real-time browser notifications"
                      checked={notifications.pushNotifications}
                      onChange={(checked) => setNotifications({ ...notifications, pushNotifications: checked })}
                    />

                    <ToggleOption
                      label="Session reminders"
                      description="Remind about incomplete sessions"
                      checked={notifications.sessionReminders}
                      onChange={(checked) => setNotifications({ ...notifications, sessionReminders: checked })}
                    />

                    <ToggleOption
                      label="Market updates"
                      description="Daily shipping market summaries"
                      checked={notifications.marketUpdates}
                      onChange={(checked) => setNotifications({ ...notifications, marketUpdates: checked })}
                    />
                  </div>
                </div>
              )}

              {/* Appearance Section */}
              {activeSection === 'appearance' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-1">Appearance</h2>
                    <p className="text-sm text-[var(--text-muted)]">Customize how DryVox looks</p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-3">Theme</label>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { value: 'light' as Theme, icon: Sun, label: 'Light' },
                          { value: 'dark' as Theme, icon: Moon, label: 'Dark' },
                          { value: 'system' as Theme, icon: Monitor, label: 'System' },
                        ].map(({ value, icon: Icon, label }) => (
                          <button
                            key={value}
                            onClick={() => setTheme(value)}
                            className={cn(
                              'flex flex-col items-center gap-2 p-4 rounded-xl text-sm font-medium transition-all',
                              theme === value
                                ? 'bg-ocean-cyan/20 text-ocean-cyan border-2 border-ocean-cyan/50'
                                : 'bg-[var(--glass-bg)] text-[var(--text-secondary)] border border-[var(--border-color)] hover:border-[var(--glass-border-hover)]'
                            )}
                          >
                            <Icon className="w-6 h-6" />
                            <span>{label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <ToggleOption
                      label="Compact mode"
                      description="Reduce spacing for more content"
                      checked={compactMode}
                      onChange={(checked) => setCompactMode(checked)}
                    />

                    <ToggleOption
                      label="Enable animations"
                      description="Show smooth transitions and effects"
                      checked={animationsEnabled}
                      onChange={(checked) => setAnimationsEnabled(checked)}
                    />
                  </div>
                </div>
              )}

              {/* Privacy Section */}
              {activeSection === 'privacy' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-white mb-1">Privacy & Security</h2>
                    <p className="text-sm text-white/50">Manage your data and security settings</p>
                  </div>

                  <div className="space-y-4">
                    {/* Memory Management */}
                    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <div className="flex items-start gap-4">
                        <Brain className="w-8 h-8 text-ocean-cyan flex-shrink-0 mt-1" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-white">AI Memory</p>
                              <p className="text-sm text-white/50">
                                {user?.has_memory
                                  ? `DryVox has learned ${user.memory_length.toLocaleString()} characters of preferences from your sessions`
                                  : 'DryVox has not yet learned preferences from your sessions'}
                              </p>
                            </div>
                          </div>
                          {user?.has_memory && (
                            <button
                              onClick={handleClearMemory}
                              disabled={clearingMemory}
                              className={cn(
                                'mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm',
                                memoryCleared
                                  ? 'bg-green-500/20 text-green-400'
                                  : 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
                              )}
                            >
                              {clearingMemory ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
                                  Clearing...
                                </>
                              ) : memoryCleared ? (
                                <>
                                  <Check className="w-4 h-4" />
                                  Memory Cleared
                                </>
                              ) : (
                                <>
                                  <Trash2 className="w-4 h-4" />
                                  Clear Memory
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-white">Change Password</p>
                          <p className="text-sm text-white/50">Update your account password</p>
                        </div>
                        <button
                          onClick={() => setShowPasswordModal(true)}
                          className="px-4 py-2 rounded-lg bg-ocean-cyan/20 text-ocean-cyan hover:bg-ocean-cyan/30 transition-colors"
                        >
                          Update
                        </button>
                      </div>
                    </div>

                    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-white">Two-Factor Authentication</p>
                          <p className="text-sm text-white/50">Add an extra layer of security</p>
                        </div>
                        <button className="px-4 py-2 rounded-lg bg-ocean-cyan/20 text-ocean-cyan hover:bg-ocean-cyan/30 transition-colors">
                          Enable
                        </button>
                      </div>
                    </div>

                    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-white">Export Data</p>
                          <p className="text-sm text-white/50">Download all your data</p>
                        </div>
                        <button
                          onClick={handleExportData}
                          disabled={exporting}
                          className="px-4 py-2 rounded-lg bg-ocean-cyan/20 text-ocean-cyan hover:bg-ocean-cyan/30 transition-colors disabled:opacity-50"
                        >
                          {exporting ? 'Exporting...' : 'Export'}
                        </button>
                      </div>
                    </div>

                    <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-red-400">Delete Account</p>
                          <p className="text-sm text-white/50">Permanently delete your account</p>
                        </div>
                        <button className="px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors">
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="mt-8 pt-6 border-t border-[var(--border-color)] flex justify-start">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className={cn(
                    'inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all',
                    saved
                      ? 'bg-green-500/20 text-green-500'
                      : saving
                        ? 'bg-white/10 text-white/50 cursor-not-allowed'
                        : 'bg-gradient-to-r from-ocean-cyan to-ocean-blue text-white hover:opacity-90'
                  )}
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : saved ? (
                    <>
                      <Check className="w-4 h-4" />
                      Saved!
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </GlassPanel>
          </motion.div>
        </div>
      </motion.div>

      {/* Password Change Modal */}
      <AnimatePresence>
        {showPasswordModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={closePasswordModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-md bg-[var(--glass-bg)] border border-[var(--border-color)] rounded-2xl p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Change Password</h3>
                <button
                  onClick={closePasswordModal}
                  className="p-1 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Success Message */}
              {passwordSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Check className="w-8 h-8 text-green-500" />
                  </div>
                  <p className="text-lg font-medium text-white">Password Changed!</p>
                  <p className="text-sm text-white/50 mt-1">Your password has been updated successfully.</p>
                </div>
              ) : (
                <>
                  {/* Error Message */}
                  {passwordError && (
                    <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-400">{passwordError}</p>
                    </div>
                  )}

                  {/* Form Fields */}
                  <div className="space-y-4">
                    {/* Current Password */}
                    <div>
                      <label className="block text-sm text-white/60 mb-2">Current Password</label>
                      <div className="relative">
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={passwordForm.currentPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                          className={cn(
                            'w-full px-4 py-3 pr-12 rounded-xl',
                            'bg-white/5 border border-white/10',
                            'text-white placeholder-white/30',
                            'focus:outline-none focus:ring-2 focus:ring-ocean-cyan/50'
                          )}
                          placeholder="Enter current password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-white/40 hover:text-white/70 transition-colors"
                        >
                          {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    {/* New Password */}
                    <div>
                      <label className="block text-sm text-white/60 mb-2">New Password</label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                          className={cn(
                            'w-full px-4 py-3 pr-12 rounded-xl',
                            'bg-white/5 border border-white/10',
                            'text-white placeholder-white/30',
                            'focus:outline-none focus:ring-2 focus:ring-ocean-cyan/50'
                          )}
                          placeholder="Enter new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-white/40 hover:text-white/70 transition-colors"
                        >
                          {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      <p className="text-xs text-white/40 mt-1">Min 8 characters with uppercase, lowercase, and number</p>
                    </div>

                    {/* Confirm New Password */}
                    <div>
                      <label className="block text-sm text-white/60 mb-2">Confirm New Password</label>
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                        className={cn(
                          'w-full px-4 py-3 rounded-xl',
                          'bg-white/5 border border-white/10',
                          'text-white placeholder-white/30',
                          'focus:outline-none focus:ring-2 focus:ring-ocean-cyan/50'
                        )}
                        placeholder="Confirm new password"
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={closePasswordModal}
                      className="flex-1 px-4 py-2.5 rounded-xl font-medium text-sm bg-white/5 text-white/70 hover:bg-white/10 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handlePasswordChange}
                      disabled={changingPassword}
                      className={cn(
                        'flex-1 px-4 py-2.5 rounded-xl font-medium text-sm transition-all',
                        'bg-gradient-to-r from-ocean-cyan to-ocean-blue text-white',
                        changingPassword ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                      )}
                    >
                      {changingPassword ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Updating...
                        </span>
                      ) : (
                        'Update Password'
                      )}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AppShell>
  );
}

// Toggle Option Component
function ToggleOption({ 
  label, 
  description, 
  checked, 
  onChange 
}: { 
  label: string; 
  description: string; 
  checked: boolean; 
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10">
      <div className="flex-1 min-w-0">
        <p className="font-medium text-white">{label}</p>
        <p className="text-sm text-white/50">{description}</p>
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={cn(
          'relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ml-4',
          checked ? 'bg-ocean-cyan' : 'bg-white/20'
        )}
      >
        <span
          className={cn(
            'absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform',
            checked ? 'left-6' : 'left-1'
          )}
        />
      </button>
    </div>
  );
}

export default Settings;

